import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { ZoomIn, ZoomOut } from '@mui/icons-material';

interface Snippet {
  id: number;
  audioFileId: number;
  regionId: string;
  startTime: number;
  endTime: number;
  name: string;
  isActive: boolean;
}

interface YouTubeTimelineProps {
  duration: number;
  currentTime: number;
  snippets: Snippet[];
  onSeek: (time: number) => void;
  onSnippetClick?: (snippet: Snippet) => void;
  width?: number | string;
  height?: number;
  startMarker?: number | null;
  endMarker?: number | null;
  zoomLevel?: number;
  onZoomChange?: (zoom: number) => void;
  showZoomControls?: boolean;
}

const YouTubeTimeline: React.FC<YouTubeTimelineProps> = ({
  duration,
  currentTime,
  snippets,
  onSeek,
  onSnippetClick,
  width = '100%',
  height = 60,
  startMarker = null,
  endMarker = null,
  zoomLevel: externalZoomLevel,
  onZoomChange,
  showZoomControls = false
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const innerTimelineRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScroll, setDragStartScroll] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  
  // Zoom state - use external if provided, otherwise internal
  const containerRef = useRef<HTMLDivElement>(null);
  const [internalZoomLevel, setInternalZoomLevel] = useState<number>(1);
  const zoomLevel = externalZoomLevel !== undefined ? externalZoomLevel : internalZoomLevel;
  const setZoomLevel = (newZoom: number) => {
    if (onZoomChange) {
      onZoomChange(newZoom);
    } else {
      setInternalZoomLevel(newZoom);
    }
  };
  const [isZooming, setIsZooming] = useState(false);
  const [showTouchHint, setShowTouchHint] = useState(false);
  const [lastManualScrollTime, setLastManualScrollTime] = useState(0);
  const isInitializedRef = useRef<boolean>(false);
  
  // Touch gesture state
  const [initialDistance, setInitialDistance] = useState<number | null>(null);
  const [initialZoom, setInitialZoom] = useState<number | null>(null);
  
  // Calculate base zoom (pixels per second to fit full duration)
  const containerWidthRef = useRef<number>(0);
  const [viewportWidth, setViewportWidth] = useState<number>(0);
  
  // Update viewport width when container size changes
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        containerWidthRef.current = width;
        setViewportWidth(width);
        // Initialize zoom to fit full duration on first load only
        if (duration > 0 && !isInitializedRef.current && width > 0) {
          const baseZoom = width / duration;
          setZoomLevel(baseZoom);
          isInitializedRef.current = true;
        }
      }
    };
    
    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [duration, width]);
  
  // Calculate timeline dimensions
  // Ensure timeline is always at least as wide as viewport, even when duration is 0
  const calculatedTimelineWidth = duration > 0 && zoomLevel > 0 ? duration * zoomLevel : 0;
  const timelineWidth = Math.max(calculatedTimelineWidth, viewportWidth || 100);
  const visibleStartTime = scrollContainerRef.current && zoomLevel > 0
    ? scrollContainerRef.current.scrollLeft / zoomLevel 
    : 0;
  const visibleEndTime = zoomLevel > 0 && viewportWidth > 0
    ? visibleStartTime + (viewportWidth / zoomLevel)
    : (duration || 0);
  
  // Filter snippets to only render visible ones (performance optimization)
  const visibleSnippets = snippets.filter(snippet => 
    snippet.endTime >= visibleStartTime && 
    snippet.startTime <= visibleEndTime
  );
  
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate distance between two touch points
  const getDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };
  
  // Handle touch start for zoom gestures
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getDistance(e.touches[0], e.touches[1]);
      setInitialDistance(distance);
      setInitialZoom(zoomLevel);
      setIsZooming(true);
    }
  }, [zoomLevel]);
  
  // Handle touch move for zoom gestures
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2 && isZooming && initialDistance !== null && initialZoom !== null) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / initialDistance;
      const currentViewportWidth = containerWidthRef.current || viewportWidth;
      if (duration > 0 && currentViewportWidth > 0) {
        const minZoom = currentViewportWidth / duration; // Fit to screen (100% view)
        const maxZoom = currentViewportWidth / 0.1; // Max: 0.1 seconds per pixel
        const newZoom = Math.max(minZoom, Math.min(maxZoom, initialZoom * scale));
        
        setZoomLevel(newZoom);
        
        // Add visual feedback
        if (containerRef.current) {
          containerRef.current.classList.add('zooming');
        }
      }
    }
  }, [isZooming, initialDistance, initialZoom, viewportWidth, duration]);
  
  // Handle touch end for zoom gestures
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (e.touches.length < 2) {
      setIsZooming(false);
      setInitialDistance(null);
      setInitialZoom(null);
      
      // Remove visual feedback
      if (containerRef.current) {
        containerRef.current.classList.remove('zooming');
      }
    }
  }, []);
  
  // Handle wheel events for zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    // Handle scroll wheel zoom with Ctrl/Cmd key
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const currentViewportWidth = containerWidthRef.current || viewportWidth;
      if (duration > 0 && currentViewportWidth > 0) {
        const minZoom = currentViewportWidth / duration; // Fit to screen (100% view)
        const maxZoom = currentViewportWidth / 0.1; // Max: 0.1 seconds per pixel
        const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
        const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel * (1 + zoomDelta)));
        setZoomLevel(newZoom);
      }
    }
  }, [zoomLevel, viewportWidth, duration]);
  
  // Set up touch and wheel event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleWheel]);
  
  // Show touch hint on mobile devices
  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTouchDevice = 'ontouchstart' in window;
    
    if ((isMobile || isTouchDevice) && duration > 0) {
      setShowTouchHint(true);
      const timer = setTimeout(() => {
        setShowTouchHint(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [duration]);
  
  // Zoom functions
  const handleZoomIn = () => {
    const currentViewportWidth = containerWidthRef.current || viewportWidth;
    if (duration > 0 && currentViewportWidth > 0) {
      const minZoom = currentViewportWidth / duration; // Fit to screen (100% view)
      const maxZoom = currentViewportWidth / 0.1; // Max: 0.1 seconds per pixel
      const newZoom = Math.min(maxZoom, Math.max(minZoom, zoomLevel * 1.5));
      setZoomLevel(newZoom);
    }
  };
  
  const handleZoomOut = () => {
    const currentViewportWidth = containerWidthRef.current || viewportWidth;
    if (duration > 0 && currentViewportWidth > 0) {
      const minZoom = currentViewportWidth / duration; // Fit to screen (100% view) - don't zoom out past this
      const maxZoom = currentViewportWidth / 0.1; // Max: 0.1 seconds per pixel
      const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel / 1.5));
      setZoomLevel(newZoom);
    }
  };
  
  const handleZoomToFit = () => {
    const currentViewportWidth = containerWidthRef.current || viewportWidth;
    if (duration > 0 && currentViewportWidth > 0) {
      const baseZoom = currentViewportWidth / duration;
      setZoomLevel(baseZoom);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = 0;
      }
    }
  };
  
  // Handle timeline hover to show timestamp
  const handleTimelineHover = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current || zoomLevel <= 0 || duration <= 0) {
      setHoverTime(null);
      setHoverPosition(null);
      return;
    }
    
    // Get position relative to the scroll container (not the inner timeline)
    const scrollRect = scrollContainerRef.current.getBoundingClientRect();
    const hoverX = event.clientX - scrollRect.left;
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    
    // Calculate time: position in scroll container + scroll offset, then convert to time
    const absolutePosition = hoverX + scrollLeft;
    const time = absolutePosition / zoomLevel;
    const clampedTime = Math.max(0, Math.min(duration, time));
    
    setHoverTime(clampedTime);
    setHoverPosition({ x: event.clientX, y: scrollRect.top - 30 });
  };
  
  const handleTimelineMouseLeave = () => {
    setHoverTime(null);
    setHoverPosition(null);
  };
  
  // Handle timeline click with zoom and scroll offset
  const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current || zoomLevel <= 0 || duration <= 0) return;
    
    // Get position relative to the scroll container (not the inner timeline)
    const scrollRect = scrollContainerRef.current.getBoundingClientRect();
    const clickX = event.clientX - scrollRect.left;
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    
    // Calculate time: position in scroll container + scroll offset, then convert to time
    const absolutePosition = clickX + scrollLeft;
    const time = absolutePosition / zoomLevel;
    const clampedTime = Math.max(0, Math.min(duration, time));
    
    onSeek(clampedTime);
  };
  
  // Handle mouse down for dragging/panning
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (timelineWidth > viewportWidth) {
      // Panning mode when zoomed in
      setIsPanning(true);
      setDragStartX(event.clientX);
      if (scrollContainerRef.current) {
        setDragStartScroll(scrollContainerRef.current.scrollLeft);
      }
    } else {
      // Seeking mode when not zoomed
      setIsDragging(true);
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsPanning(false);
  };
  
  // Calculate effective timeline width for panning
  const effectiveTimelineWidthForPan = Math.max(timelineWidth, viewportWidth || 100);
  
  // Handle mouse move for dragging/panning
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isPanning && scrollContainerRef.current && viewportWidth > 0) {
      const deltaX = dragStartX - event.clientX;
      const newScrollLeft = dragStartScroll + deltaX;
      scrollContainerRef.current.scrollLeft = Math.max(0, Math.min(effectiveTimelineWidthForPan - viewportWidth, newScrollLeft));
    } else if (isDragging && scrollContainerRef.current && zoomLevel > 0 && duration > 0) {
      // Get position relative to the scroll container
      const scrollRect = scrollContainerRef.current.getBoundingClientRect();
      const clickX = event.clientX - scrollRect.left;
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      
      // Calculate time: position in scroll container + scroll offset, then convert to time
      const absolutePosition = clickX + scrollLeft;
      const time = absolutePosition / zoomLevel;
      const clampedTime = Math.max(0, Math.min(duration, time));
      onSeek(clampedTime);
    }
  };
  
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setIsPanning(false);
    };
    
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);
  
  // Track manual scrolling
  const handleScroll = () => {
    setLastManualScrollTime(Date.now());
  };
  
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);
  
  // Auto-scroll to keep current time visible during playback
  useEffect(() => {
    if (!scrollContainerRef.current || !innerTimelineRef.current || duration <= 0 || zoomLevel <= 0 || viewportWidth <= 0) return;
    
    // Only auto-scroll if user hasn't manually scrolled recently (within last 2 seconds)
    const timeSinceManualScroll = Date.now() - lastManualScrollTime;
    if (timeSinceManualScroll < 2000) return;
    
    const currentTimePosition = currentTime * zoomLevel;
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const scrollRight = scrollLeft + viewportWidth;
    const effectiveWidth = Math.max(timelineWidth, viewportWidth);
    
    // Check if current time is outside visible viewport
    if (currentTimePosition < scrollLeft || currentTimePosition > scrollRight) {
      // Center current time in viewport
      const targetScroll = currentTimePosition - viewportWidth / 2;
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, Math.min(effectiveWidth - viewportWidth, targetScroll)),
        behavior: 'smooth'
      });
    }
  }, [currentTime, zoomLevel, viewportWidth, timelineWidth, duration, lastManualScrollTime]);
  
  // Calculate positions for rendering
  const progressPosition = duration > 0 && zoomLevel > 0 ? currentTime * zoomLevel : 0;
  const progressWidth = duration > 0 ? (currentTime / duration) * timelineWidth : 0;
  
  // Ensure timeline is always visible - use viewport width as minimum if duration not loaded
  const effectiveTimelineWidth = Math.max(timelineWidth, viewportWidth || 100);
  
  return (
    <Box
      ref={containerRef}
      sx={{
        width: width,
        height: height || 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        position: 'relative',
        userSelect: 'none',
        visibility: 'visible',
        opacity: 1
      }}
    >
      {/* Zoom controls - only show if showZoomControls is true */}
      {showZoomControls && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            justifyContent: 'flex-end',
            mb: -0.5,
            height: 'auto'
          }}
        >
          <Tooltip title="Zoom Out">
            <IconButton
              size="small"
              onClick={handleZoomOut}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 1)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ZoomOut fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom To Fit">
            <IconButton
              size="small"
              onClick={handleZoomToFit}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 1)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Fit
              </Typography>
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom In">
            <IconButton
              size="small"
              onClick={handleZoomIn}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 1)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ZoomIn fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      
      {/* Scrollable container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          position: 'relative',
          '&::-webkit-scrollbar': {
            height: '6px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)'
            }
          }
        }}
      >
        {/* Inner timeline with dynamic width */}
        <Box
          ref={innerTimelineRef}
          sx={{
            position: 'relative',
            width: `${effectiveTimelineWidth}px`,
            minWidth: '100%',
            height: 20,
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 1,
            cursor: (viewportWidth > 0 && effectiveTimelineWidth > viewportWidth) ? 'grab' : 'pointer',
            border: '1px solid rgba(85, 203, 205, 0.2)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.15)'
            },
            '&:active': {
              cursor: (viewportWidth > 0 && effectiveTimelineWidth > viewportWidth) ? 'grabbing' : 'pointer'
            }
          }}
          onClick={handleTimelineClick}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={(e) => {
            handleMouseMove(e);
            handleTimelineHover(e);
          }}
          onMouseLeave={handleTimelineMouseLeave}
        >
          {/* Progress bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: duration > 0 ? `${progressWidth}px` : '0px',
              bgcolor: 'rgba(85, 203, 205, 0.8)',
              borderRadius: 1,
              transition: 'width 0.1s ease',
              minWidth: duration > 0 ? '2px' : '0px'
            }}
          />
          
          {/* Loading/placeholder state */}
          {duration <= 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.75rem'
              }}
            >
              Loading timeline...
            </Box>
          )}
          
          {/* Snippet markers */}
          {visibleSnippets.map((snippet) => {
            if (!duration || duration <= 0) return null;
            
            const startPosition = snippet.startTime * zoomLevel;
            const endPosition = snippet.endTime * zoomLevel;
            const snippetWidth = endPosition - startPosition;
            
            return (
              <Tooltip
                key={snippet.id}
                title={`${snippet.name} (${formatTime(snippet.startTime)} - ${formatTime(snippet.endTime)})`}
                placement="top"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: `${startPosition}px`,
                    width: `${snippetWidth}px`,
                    minWidth: '2px',
                    height: '100%',
                    bgcolor: snippet.isActive ? 'rgba(249, 145, 146, 0.6)' : 'rgba(80, 80, 80, 0.6)',
                    opacity: 0.7,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSeek(snippet.startTime);
                    onSnippetClick?.(snippet);
                  }}
                >
                  {snippetWidth > 30 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        textAlign: 'left',
                        lineHeight: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '90%',
                        userSelect: 'none',
                        paddingLeft: '4px'
                      }}
                    >
                      {snippet.name}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
          
          {/* Start marker */}
          {startMarker !== null && duration > 0 && (
            <Tooltip title={`Start Marker: ${formatTime(startMarker)}`} placement="top">
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  left: `${startMarker * zoomLevel}px`,
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '12px solid rgba(255, 255, 0, 0.8)',
                  transform: 'translateX(-50%)',
                  zIndex: 3,
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSeek(startMarker);
                }}
              />
            </Tooltip>
          )}
          
          {/* End marker */}
          {endMarker !== null && duration > 0 && (
            <Tooltip title={`End Marker: ${formatTime(endMarker)}`} placement="top">
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  left: `${endMarker * zoomLevel}px`,
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '12px solid rgba(255, 0, 0, 0.8)',
                  transform: 'translateX(-50%)',
                  zIndex: 3,
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSeek(endMarker);
                }}
              />
            </Tooltip>
          )}
          
          {/* Current time indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              left: `${progressPosition}px`,
              width: 4,
              height: 24,
              bgcolor: 'rgba(249, 113, 113, 1)',
              borderRadius: 1,
              transform: 'translateX(-50%)',
              zIndex: 2,
              pointerEvents: 'none'
            }}
          />
          
          {/* Hover timestamp indicator */}
          {hoverTime !== null && hoverPosition && (
            <Box
              sx={{
                position: 'fixed',
                left: `${hoverPosition.x}px`,
                top: `${hoverPosition.y}px`,
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: 1,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                zIndex: 1000,
                pointerEvents: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
              }}
            >
              {formatTime(hoverTime)}
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Time labels */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          {formatTime(visibleStartTime)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          {formatTime(visibleEndTime)} / {formatTime(duration)}
        </Typography>
      </Box>
      
      {/* Zoom indicator */}
      {isZooming && duration > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 1,
            zIndex: 10,
            pointerEvents: 'none',
            fontSize: '1.2rem',
            fontWeight: 'bold'
          }}
        >
          {viewportWidth > 0 
            ? `${Math.round((zoomLevel / (viewportWidth / duration)) * 10) / 10}x`
            : '1x'}
        </Box>
      )}
      
      {/* Touch zoom hint */}
      {showTouchHint && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: 1,
            zIndex: 10,
            pointerEvents: 'none',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}
        >
          Pinch to zoom • Use two fingers
        </Box>
      )}
    </Box>
  );
};

export default YouTubeTimeline;
